import { AgentType } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import type { TypedFormState } from '@utima/ui-informed';
import { useFieldState } from 'informed';
import { memo } from 'react';

import showCalloutsTooltipImg from '@/assets/images/forms/show-callouts-tooltip.webp';
import { Card } from '@/components/card/Card';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';

export const PersonalizationFields = memo(() => {
  const { t } = useLingui();
  const typeFieldState = useFieldState('agentType');

  const { schema } = useFieldsSchema(
    [
      {
        name: 'chatbotConfig.initialMessage',
        label: t`Initial message`,
        type: 'richtext',
        defaultValue: t`Hello, I'm AI Honza! How can I help you today?`,
        showOptional: false,
      },
      {
        name: 'chatbotConfig.showPopup',
        label: t`Show bubble popup`,
        type: 'checkbox',
        uiControl: 'switch',
        defaultValue: true,
        showOptional: false,
      },
      {
        $relevant: {
          $when: ({ formState }) =>
            (formState?.values as any)?.chatbotConfig?.showPopup,
          $fields: [
            {
              name: 'chatbotConfig.bubbleTitle',
              label: t`Bubble title`,
              type: 'text',
              defaultValue: t`Hello, I'm AI Honza!`,
              showOptional: false,
            },
            {
              name: 'chatbotConfig.bubbleDescription',
              label: t`Bubble description`,
              type: 'text',
              defaultValue: t`Ask me anything!`,
              showOptional: false,
            },
            {
              name: 'chatbotConfig.showPopupMobile',
              label: t`Show bubble popup on mobile`,
              type: 'checkbox',
              uiControl: 'switch',
              defaultValue: false,
              showOptional: false,
            },
          ],
        },
      },
      {
        name: 'chatbotConfig.openOnLoad',
        label: t`Open chat automatically`,
        type: 'checkbox',
        uiControl: 'switch',
        defaultValue: false,
        showOptional: false,
        uiProps: {
          tooltip: (
            <div className='flex max-w-80 flex-col gap-2'>
              <h4 className='text-base font-bold'>{t`Open chat automatically`}</h4>
              <p className='text-sm'>
                <Trans>
                  Opens the chat dialog as soon as the page loads. Useful for
                  dedicated landing pages or QR links where the chatbot should
                  appear immediately without waiting for the visitor to click
                  the bubble.
                </Trans>
              </p>
            </div>
          ),
        },
      },
      {
        name: 'chatbotConfig.showFeedbackButtons',
        label: t`Show buttons for collecting feedback`,
        type: 'checkbox',
        uiControl: 'switch',
        defaultValue: true,
        showOptional: false,
      },
      {
        name: 'chatbotConfig.showCallouts',
        label: t`Show callouts`,
        type: 'checkbox',
        uiControl: 'switch',
        uiProps: {
          tooltip: (
            <div className='flex max-w-80 flex-col gap-2'>
              <h4 className='text-base font-bold'>{t`Show Callouts`}</h4>
              <p className='text-sm'>
                <Trans>
                  Callouts are messages that are displayed above the minimized
                  chat to engage with user and inform them about the existence
                  of the chatbot.
                </Trans>
              </p>
              <img
                className='mx-auto rounded-lg border border-border text-sm shadow-md'
                src={showCalloutsTooltipImg}
                alt='Show callouts'
              />
              <p>
                <Trans>
                  The are <strong>context-aware</strong> and will display
                  personalized messages based on currently visited page
                  (assuming the page is available in the agent&apos;s data
                  source)
                </Trans>
              </p>
            </div>
          ),
        },
        defaultValue: true,
        showOptional: false,
      },
      {
        $relevant: {
          $when: ({ formState }) =>
            !!(
              formState as TypedFormState<{
                chatbotConfig?: { showCallouts?: boolean };
              }>
            ).values?.chatbotConfig?.showCallouts,
          $fields: [
            {
              name: 'chatbotConfig.calloutSoundEnabled',
              label: t`Play sound for callouts`,
              tooltip: t`Plays a short sound when a callout appears. Browsers only allow it after the visitor has interacted with the page.`,
              type: 'checkbox',
              uiControl: 'switch',
              defaultValue: true,
              showOptional: false,
            },
          ],
        },
      },
    ],
    [t],
  );

  // These fields should be visible only if the agent type is Chat
  if (typeFieldState.value !== AgentType.Chat) {
    return null;
  }

  return (
    <Card title={t`Personalization`}>
      <SchemaFields schema={schema} />
    </Card>
  );
});
